import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { PostDeployControl } from "@/components/PostDeployControl";
import { Skeleton } from "@/components/ui/skeleton";
import { PenSquare, ExternalLink } from "lucide-react";
import { BLOG_PAGE, AUTHORED_POSTS, FALLBACK_LIVE_POSTS } from "@/data/blogContent";

const stripHtml = (html) => (html || "").replace(/<[^>]+>/g, "").trim();

const fmtDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch (e) {
    return "";
  }
};

export default function BlogPage() {
  const B = BLOG_PAGE;
  const [livePosts, setLivePosts] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get("https://www.slingshotseo.com/wp-json/wp/v2/posts", {
        params: { per_page: 6, _fields: "id,title,excerpt,link,date" },
        timeout: 12000,
      })
      .then(({ data }) => !cancelled && setLivePosts(data))
      .catch(() => !cancelled && (setLoadFailed(true), setLivePosts(FALLBACK_LIVE_POSTS)));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div data-testid="blog-page" className="bg-white">
      <SiteHeader />
      <main>
        {/* Hero */}
        <section data-testid="blog-hero" className="relative overflow-hidden bg-[#0b0b0c] hero-noise">
          <img src={B.hero.bg} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(90deg, rgba(2,2,2,0.86) 0%, rgba(2,2,2,0.62) 50%, rgba(2,2,2,0.86) 100%)" }}
          />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 min-h-[420px] md:min-h-[460px] flex items-center justify-center pt-32 pb-14 md:pt-36 md:pb-16">
            <div className="text-center max-w-3xl mx-auto">
              <p className="font-nunito text-[11px] sm:text-xs font-extrabold uppercase tracking-[0.3em] text-[#ff6b63] mb-5">
                {B.hero.eyebrow}
              </p>
              <h1 data-testid="blog-hero-title" className="font-nunito font-extrabold text-white text-4xl sm:text-5xl leading-[1.08] tracking-tight">
                {B.hero.titlePre}
                <span className="text-[#ee2e24]">{B.hero.titleAccent}</span>
              </h1>
              <p className="mt-5 text-[#dedede] text-sm sm:text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
                {B.hero.sub}
              </p>
            </div>
          </div>
        </section>

        {/* New drafts from this project (deployable) */}
        <section data-testid="blog-authored" className="bg-[#fafafa] border-b border-[#f1f1f1] py-14 md:py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-3">
              <PenSquare size={16} className="text-[#ee2e24]" />
              <h2 className="font-nunito text-xl font-extrabold text-[#383838]">New from the refresh project</h2>
              <span className="rounded-full bg-[#ee2e24]/[0.08] border border-[#ee2e24]/20 px-2.5 py-0.5 font-nunito text-[10px] font-extrabold uppercase tracking-[0.1em] text-[#ee2e24]">
                Deployable
              </span>
            </div>
            <p className="mt-2 text-[13px] text-[#8a8a8a]">
              Posts written here get a deploy control — push as a WP draft, or publish live with confirmation. Existing live posts below never show these buttons.
            </p>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {AUTHORED_POSTS.map((post) => (
                <article
                  key={post.slug}
                  data-testid={`blog-authored-card-${post.slug}`}
                  className="rounded-[14px] border border-[#ececec] bg-white overflow-hidden shadow-[0_6px_18px_rgba(2,2,2,0.04)] hover:shadow-[0_14px_40px_rgba(2,2,2,0.1)] transition-shadow duration-200"
                >
                  <Link to={`/blog/${post.slug}`} className="block">
                    <img src={post.image} alt="" className="h-44 w-full object-cover" loading="lazy" />
                  </Link>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-[11px] font-nunito font-extrabold uppercase tracking-[0.12em] text-[#8a8a8a]">
                      <span className="text-[#ee2e24]">{post.category}</span>
                      <span>·</span>
                      <span>{fmtDate(post.date)}</span>
                      <span>·</span>
                      <span>{post.readMinutes} min read</span>
                    </div>
                    <Link to={`/blog/${post.slug}`}>
                      <h3 className="mt-3 font-nunito text-lg font-extrabold text-[#383838] leading-snug hover:text-[#ee2e24] transition-colors duration-150">
                        {post.title}
                      </h3>
                    </Link>
                    <p className="mt-2 text-[13px] text-[#6e6e6e] leading-relaxed">{post.excerpt}</p>
                    <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                      <Link
                        to={`/blog/${post.slug}`}
                        data-testid={`blog-read-link-${post.slug}`}
                        className="font-nunito text-[12px] font-extrabold uppercase tracking-[0.1em] text-[#ee2e24] hover:underline"
                      >
                        Read the mockup →
                      </Link>
                      <PostDeployControl slug={post.slug} title={post.title} />
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Existing live posts (no deploy buttons) */}
        <section data-testid="blog-live-feed" className="bg-white py-14 md:py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <h2 className="font-nunito text-xl font-extrabold text-[#383838]">From the live blog</h2>
            <p className="mt-2 text-[13px] text-[#8a8a8a]">
              {loadFailed ? "Live feed unavailable right now." : "Pulled directly from slingshotseo.com — read-only."}
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {livePosts === null
                ? [1, 2, 3].map((i) => (
                    <div key={i} className="rounded-[12px] border border-[#ececec] p-6">
                      <Skeleton className="h-4 w-24 bg-[#f0f0f0]" />
                      <Skeleton className="mt-3 h-5 w-full bg-[#f0f0f0]" />
                      <Skeleton className="mt-2 h-5 w-3/4 bg-[#f0f0f0]" />
                      <Skeleton className="mt-4 h-12 w-full bg-[#f0f0f0]" />
                    </div>
                  ))
                : livePosts.map((post) => (
                    <a
                      key={post.id}
                      href={post.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`blog-live-card-${post.id}`}
                      className="block rounded-[12px] border border-[#ececec] bg-white p-6 shadow-[0_6px_18px_rgba(2,2,2,0.04)] hover:-translate-y-1 hover:shadow-[0_14px_40px_rgba(2,2,2,0.1)] hover:border-[#ee2e24]/30 transition-transform duration-200"
                    >
                      {post.date && (
                        <p className="font-nunito text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#8a8a8a]">
                          {fmtDate(post.date)}
                        </p>
                      )}
                      <h3 className="mt-2 font-nunito text-[15px] font-extrabold text-[#383838] leading-snug">
                        {stripHtml(post.title?.rendered)}
                      </h3>
                      <p className="mt-2 text-[13px] text-[#6e6e6e] leading-relaxed line-clamp-3">
                        {stripHtml(post.excerpt?.rendered)}
                      </p>
                      <span className="mt-3 inline-flex items-center gap-1 font-nunito text-[12px] font-extrabold uppercase tracking-[0.1em] text-[#ee2e24]">
                        Read on the live site <ExternalLink size={12} />
                      </span>
                    </a>
                  ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
